/* oxlint-disable max-lines */
import { VariableSchema as DataVariableSchema } from '@omnea-digital/data-variables'
import { primaryOutputConfigSchema } from '@omnea-digital/extops-schema'
import { z } from 'zod'
import { ref, refsAt } from '@omnea-app/packages/universal/references/refKinds'
import { AssigneeTypeEnum } from '@omnea-app/packages/universal/users-and-groups/assigneeType'
import { ComparisonSchema } from '../conditionality/schema'
import { globalIntegrations } from '../integrations/globalIntegrations'
import {
  AgentConfigV2Schema,
  BlockAgentSchema,
  ContentGenerationBlockAgentSchema,
  LegacyAgentConfigSchema,
} from '../task-agents/agentContracts'
import { AssigneeEntitySchema } from './assigneeEntity'
import { outputMappingSchema } from './blockOutputMapping'
import { BlockTypeEnum } from './blockType'
import { TransformationOutputFieldSchema } from './dataTransformation'
import {
  externalOperationHttpMappingSchema,
  externalOperationSftpMappingSchema,
  httpHeaderSchema,
  type HttpHeader,
} from './externalOperationHttpMapping'
import { externalOperationInputMappingSchema } from './externalOperationInputMapping'
import { externalOperationOutputMappingSchema } from './externalOperationOutputMapping'
import { PollingConfigurationSchema } from './externalOperationPolling'
import { WorkflowFileReferenceSchema } from './fileReferences'
import { SubWorkflowAttachmentSchema } from './subWorkflowAttachment'
import { TaskDistributionSchema } from './taskDistribution'
import { BlockTaskSchema, FillInFormTaskSchema, RfxContentGenerationTaskSchema } from './taskTypes'

// The set ids here are spelt as a dynamic-option question spells its `dataSource`, which is what lets
// a guard join a variable's core data to a form's.
export const VariableSchema = refsAt(DataVariableSchema, {
  'primitiveValidation.allowedEntityId': 'coreDataSet',
  'defaultValue.entityId': 'coreDataSet',
  'defaultValue.omneaId': { kind: 'coreDataOption', scopeFrom: 'entityId' },
})

const baseBlockConfig = { isStartingBlock: z.boolean().optional() }

const LabelOnlyBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    description: z.string().optional(),
  }),
})

type LabelOnlyBlockConfig = z.infer<typeof LabelOnlyBlockConfigSchema>

type BlockConfigByType = {
  [BlockTypeEnum.APPROVAL]: ApprovalBlockConfig
  [BlockTypeEnum.ENGAGEMENT]: EngagementBlockConfig
  [BlockTypeEnum.INTAKE]: IntakeBlockConfig
  [BlockTypeEnum.NOTIFICATION]: NotificationBlockConfig
  [BlockTypeEnum.RENEWAL_ASSESSMENT]: RenewalAssessmentBlockConfig
  [BlockTypeEnum.SUPPLIER_ONBOARDING]: SupplierOnboardingBlockConfig
  [BlockTypeEnum.BUDGET_APPROVAL]: BudgetApprovalBlockConfig
  [BlockTypeEnum.HTTP]: HttpBlockConfig
  [BlockTypeEnum.EXTERNAL_OPERATION]: ExternalOperationBlockConfig
  [BlockTypeEnum.EXTERNAL_PROCEDURE]: ExternalProcedureBlockConfig
  [BlockTypeEnum.CREATE_RENEWAL]: CreateRenewalBlockConfig
  [BlockTypeEnum.COMMIT_SUPPLIER_DATA]: CommitSupplierDataBlockConfig
  [BlockTypeEnum.CREATE_AGREEMENT]: CreateAgreementBlockConfig
  [BlockTypeEnum.CANCEL_AGREEMENT]: CancelAgreementBlockConfig
  [BlockTypeEnum.CANCEL_ASSESSMENTS]: CancelAssessmentsBlockConfig
  [BlockTypeEnum.CREATE_PO]: CreatePOBlockConfig
  [BlockTypeEnum.RFX]: RfxBlockConfig
  [BlockTypeEnum.AUCTION]: AuctionBlockConfig
  [BlockTypeEnum.CREATE_ASSESSMENT]: CreateAssessmentBlockConfig
  [BlockTypeEnum.PRICE_BENCHMARK]: PriceBenchmarkBlockConfig
  [BlockTypeEnum.DATA_TRANSFORMATION]: DataTransformationBlockConfig
  [BlockTypeEnum.WAITING]: WaitingBlockConfig
  [BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES]: SupplierOnlineResourcesBlockConfig
  [BlockTypeEnum.INHERENT_RISK_ASSESSMENT]: InherentRiskAssessmentBlockConfig
  [BlockTypeEnum.NOTIFY_SLACK]: NotifySlackBlockConfig
  [BlockTypeEnum.CHILD_REQUEST_START]: LabelOnlyBlockConfig
  [BlockTypeEnum.SUB_WORKFLOW_START]: LabelOnlyBlockConfig
  [BlockTypeEnum.MILESTONE]: LabelOnlyBlockConfig
}

// Gets a config by type, if Type is unset, gets a union of all block configs
export type BlockConfig<Type extends BlockTypeEnum = BlockTypeEnum> = BlockConfigByType[Type]

export const SkipSchema = z.object({
  skippedAt: z.string(),
  skippedBy: z.string(),
  comment: z.string().optional(),
  commentId: z.string().optional(),
})

export type Skip = z.infer<typeof SkipSchema>

export enum AssessmentType {
  EXTERNAL = 'EXTERNAL',
  INTERNAL = 'INTERNAL',
}

export const NotificationAttachmentSchema = WorkflowFileReferenceSchema

export type NotificationAttachment = z.infer<typeof NotificationAttachmentSchema>

// `subject` and `body` carry embedded mentions naming questions and request variables. The
// annotation vocabulary declares whole fields, not spans inside one, so those go undeclared.
export const NotificationMessageSchema = z.object({
  subject: z.string(),
  body: z.string(),
  attachments: z.array(NotificationAttachmentSchema).optional(),
  ccRecipients: z.array(AssigneeEntitySchema).optional(),
})

export type NotificationMessage = z.infer<typeof NotificationMessageSchema>

// Either shape is valid on read: writers emit V2, older stored configs are flat.
const AgentConfigSchema = z.union([LegacyAgentConfigSchema, AgentConfigV2Schema])

export const DataTransformationBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    outputs: z.array(TransformationOutputFieldSchema),
    description: z.string().optional(),
    shouldMapOutputToForm: z.boolean().optional(),
    outputMapping: outputMappingSchema.optional(),
    tasks: z.array(BlockTaskSchema).optional(),
    assignees: z.array(AssigneeEntitySchema).optional(),
    taskDistribution: TaskDistributionSchema.optional(),
    skip: SkipSchema.optional(),
  }),
})

export type DataTransformationBlockConfig = z.infer<typeof DataTransformationBlockConfigSchema>

// WAITING
export const WaitingBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    description: z.string().optional(),
    condition: ComparisonSchema,
    shouldMapOutputToForm: z.boolean().optional(),
    outputMapping: outputMappingSchema.optional(),
    tasks: z.array(BlockTaskSchema).optional(),
    assignees: z.array(AssigneeEntitySchema).optional(),
    taskDistribution: TaskDistributionSchema.optional(),
  }),
})

export type WaitingBlockConfig = z.infer<typeof WaitingBlockConfigSchema>

// INTAKE
export const RequestNamingModeSchema = z.enum(['automatic', 'template'])
export type RequestNamingMode = z.infer<typeof RequestNamingModeSchema>

export const IntakeBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    // Older intake blocks don't store it; readers treat absent as no tasks.
    tasks: z.array(BlockTaskSchema).optional(),
    description: z.string().optional(),
    // Misnamed: actually used as the description text on the request-name field, not the input placeholder
    requestNamePlaceholder: z.string().optional(),
    // Absent means 'automatic'. 'template' keeps requestNameTemplate active; switching back to
    // 'automatic' leaves the template stored but unused, so flipping modes is lossless.
    requestNamingMode: RequestNamingModeSchema.optional(),
    requestNameTemplate: z.string().optional(),
  }),
})

export type IntakeBlockConfig = z.infer<typeof IntakeBlockConfigSchema>

// Renewal Assessment
export const RenewalAssessmentBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    targetTimeValue: z.number().nullable(),
    assignees: z.array(AssigneeEntitySchema),
    tasks: z.array(BlockTaskSchema),
    notificationMessage: NotificationMessageSchema,
    description: z.string().optional(),
    escalateToManager: z.boolean().optional(),
  }),
})

export type RenewalAssessmentBlockConfig = z.infer<typeof RenewalAssessmentBlockConfigSchema>

// Supplier Onboarding
export const SupplierOnboardingBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    targetTimeValue: z.number().nullable(),
    assignees: z.array(AssigneeEntitySchema).optional(),
    hasStaticAssignees: z.boolean().optional(),
    taskDistribution: TaskDistributionSchema.optional(),
    tasks: z.array(BlockTaskSchema),
    notificationMessage: NotificationMessageSchema,
    emptyCommentsMessage: z.string().optional(),
    skip: SkipSchema.optional(),
    description: z.string().optional(),
    prefillFromSupplierRecord: z.boolean().optional(),
    prefillFromSupplierRecordOnSupplierProductMatch: z.boolean().optional(),
    // Blocks saved before assessment types existed don't store it; absent reads as EXTERNAL.
    assessmentType: z.enum(AssessmentType).optional(),
    escalateToManager: z.boolean().optional(),
    shouldCompleteAutomatically: z.boolean().optional(),
  }),
})

export type SupplierOnboardingBlockConfig = z.infer<typeof SupplierOnboardingBlockConfigSchema>

// Approval
export const ApprovalBlockConfigPayloadSchema = z.object({
  label: z.string(),
  assignees: z.array(AssigneeEntitySchema),
  taskDistribution: TaskDistributionSchema.optional(),
  targetTimeValue: z.number().nullable(),
  notificationMessage: NotificationMessageSchema,
  tasks: z.array(BlockTaskSchema).optional(),
  skip: SkipSchema.optional(),
  description: z.string().optional(),
  prefillFromSupplierRecord: z.boolean().optional(),
  prefillFromSupplierRecordOnSupplierProductMatch: z.boolean().optional(),
  agentConfig: AgentConfigSchema.optional(),
  agents: z.array(BlockAgentSchema).optional(),
  escalateToManager: z.boolean().optional(),
  allowApproversToRejectRequest: z.boolean().optional(),
})

export const ApprovalBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: ApprovalBlockConfigPayloadSchema,
})

export type ApprovalBlockConfig = z.infer<typeof ApprovalBlockConfigSchema>

// Engagement
export const EngagementBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    assignees: z.array(AssigneeEntitySchema),
    taskDistribution: TaskDistributionSchema.optional(),
    targetTimeValue: z.number().nullable(),
    description: z.string().optional(),
    notificationMessage: NotificationMessageSchema,
  }),
})

export type EngagementBlockConfig = z.infer<typeof EngagementBlockConfigSchema>

export const BudgetApprovalNotificationMessageSchema = z.object({
  approve: NotificationMessageSchema,
  notify: NotificationMessageSchema,
})

export type BudgetApprovalNotificationMessage = z.infer<typeof BudgetApprovalNotificationMessageSchema>

// Budget Approval
export const BudgetApprovalBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    targetTimeValue: z.number().nullable(),
    tasks: z.array(BlockTaskSchema),
    notificationMessage: BudgetApprovalNotificationMessageSchema,
    budgetApprovalGroupId: ref('budgetApprovalGroup'),
    skipIfRequestor: z.boolean(),
    skipIfAlreadyApproved: z.boolean(),
    description: z.string().optional(),
    agentConfig: AgentConfigSchema.optional(),
    agents: z.array(BlockAgentSchema).optional(),
    prefillFromSupplierRecord: z.boolean().optional(),
    prefillFromSupplierRecordOnSupplierProductMatch: z.boolean().optional(),
    escalateToManager: z.boolean().optional(),
    allowApproversToRejectRequest: z.boolean().optional(),
  }),
})

export type BudgetApprovalBlockConfig = z.infer<typeof BudgetApprovalBlockConfigSchema>

// Http block
export type { HttpHeader }
export const HttpBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    authToken: z.string().nullable(),
    url: z.string(),
    headers: z.array(httpHeaderSchema).optional(),
    description: z.string().optional(),
  }),
})

export type HttpBlockConfig = z.infer<typeof HttpBlockConfigSchema>

export const CommitSupplierDataBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    assignees: z.array(AssigneeEntitySchema),
    taskDistribution: TaskDistributionSchema.optional(),
    tasks: z.array(BlockTaskSchema),
    targetTimeValue: z.number().nullable(),
    notificationMessage: NotificationMessageSchema,
    skip: SkipSchema.optional(),
    description: z.string().optional(),
    syncWithAccountingIntegration: z.boolean().optional(),
    forceManualCompletion: z.boolean().optional(),
    escalateToManager: z.boolean().optional(),
  }),
})

export type CommitSupplierDataBlockConfig = z.infer<typeof CommitSupplierDataBlockConfigSchema>

// Auction settings shared between the standalone Auction block and the RFx block's
// end-of-RFx auction (BUY-1176) — same fields, same builder editor in both places.
export const SharedAuctionSettingsSchema = z.object({
  currency: z.string(),
  targetTimeValue: z.number().nullable(),
  showLotRank: z.boolean(),
  showLowestPricePerLot: z.boolean(),
  showStartingPrice: z.boolean(),
  notificationMessage: NotificationMessageSchema,
})

export type SharedAuctionSettings = z.infer<typeof SharedAuctionSettingsSchema>

const RfxAuctionSchema = z.discriminatedUnion('isAllowed', [
  z.object({ isAllowed: z.literal(false) }),
  z.object({ isAllowed: z.literal(true), settings: SharedAuctionSettingsSchema }),
])

// Supplier sources allowed for RFx suppliers
export const rfxSupplierSources = ['PREFERRED'] as const
export const RfxSupplierSourceSchema = z.enum(rfxSupplierSources)
export type RfxSupplierSource = z.infer<typeof RfxSupplierSourceSchema>

export const RfxSupplierFiltersSchema = z.object({ allowedSources: z.array(RfxSupplierSourceSchema) })
export type RfxSupplierFilters = z.infer<typeof RfxSupplierFiltersSchema>

// A FORM entry must also appear in `defaultFormTemplateIds`. `weight` is a percentage of the top-level
// pool, null the auto share; zero is unrepresentable, matching `rfx_criteria_weight_positive`.
export const RfxEvaluationCriterionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('FORM'), formTemplateId: ref('formTemplate'), weight: z.number().positive().nullable() }),
  z.object({
    kind: z.literal('SCORECARD'),
    scorecardTemplateId: ref('scorecardTemplate'),
    weight: z.number().positive().nullable(),
  }),
])

export type RfxEvaluationCriterion = z.infer<typeof RfxEvaluationCriterionSchema>

export const RfxEvaluationSchema = z.object({
  scoringGranularity: z.enum(['FORM', 'SECTION', 'QUESTION']).optional(),
  criteria: z.array(RfxEvaluationCriterionSchema),
})

export type RfxEvaluation = z.infer<typeof RfxEvaluationSchema>

export const RfxBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    assignees: z.array(AssigneeEntitySchema),
    taskDistribution: TaskDistributionSchema.optional(),
    notificationMessage: NotificationMessageSchema,
    defaultFormTemplateIds: z.array(ref('formTemplate')),
    // Superseded by `evaluation`, which carries weights — kept because older stored configs use it.
    scorecardTemplateIds: z.array(ref('scorecardTemplate')).optional(),
    // Behind the RELEASE_RFX_EVALUATION_PAGE flag. Read in preference to `scorecardTemplateIds`.
    evaluation: RfxEvaluationSchema.optional(),
    targetTimeValue: z.number().nullable(),
    skip: SkipSchema.optional(),
    description: z.string().optional(),
    requiresConfidentialityAgreement: z.boolean().optional(),
    prefillFromSupplierRecord: z.boolean().optional(),
    prefillFromSupplierRecordOnSupplierProductMatch: z.boolean().optional(),
    subWorkflowIds: z.array(ref('subWorkflow')).optional(),
    subWorkflows: z.array(SubWorkflowAttachmentSchema).optional(),
    // Behind the AUCTIONS flag: run an auction in place at the end of the RFx.
    auction: RfxAuctionSchema.optional(),
    // Behind the RFX_AUTONOMOUS_SOURCING flag: when true, the block auto-runs the autonomous setup
    // pipeline. Optional — older frozen configs predate it, and undefined reads as off.
    autonomousSourcing: z.boolean().optional(),
    supplierAttachments: z.array(WorkflowFileReferenceSchema).optional(),
    // Filters restricting which suppliers may be added to the RFx
    supplierFilters: RfxSupplierFiltersSchema.optional(),
    tasks: z.array(RfxContentGenerationTaskSchema).optional(),
    agents: z.array(ContentGenerationBlockAgentSchema).optional(),
  }),
})

export type RfxBlockConfig = z.infer<typeof RfxBlockConfigSchema>

// Auction Block — design-time config the workflow-builder sets (BUY-962).
// execute() only consumes label/assignees/currency at block entry; the duration
// and visibility-flag defaults are read later by the auctions service when lots
// are created (they live on auction_lots.settings, which has no row at SETUP).
export const AuctionBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.intersection(
    SharedAuctionSettingsSchema,
    z.object({
      label: z.string(),
      assignees: z.array(AssigneeEntitySchema),
      skip: SkipSchema.optional(),
      description: z.string().optional(),
    })
  ),
})

export type AuctionBlockConfig = z.infer<typeof AuctionBlockConfigSchema>

// Create Assessment Block
export const TargetStartDateTypeSchema = z.enum(['exact', 'relative', 'relativeToRequestCreatedAt'])
export type TargetStartDateType = z.infer<typeof TargetStartDateTypeSchema>

export const CreateAssessmentBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    targetTimeValue: z.number().nullable(),
    assignees: z.array(AssigneeEntitySchema),
    taskDistribution: TaskDistributionSchema.optional(),
    notificationMessage: NotificationMessageSchema,
    shouldCompleteAutomatically: z.boolean(),
    tasks: z.array(FillInFormTaskSchema),
    // Older blocks don't store it; readers treat absent as no workflow chosen, like the builder's ''.
    workflowId: ref('workflow').optional(),
    assessmentAssignees: z.array(AssigneeEntitySchema),
    frequency: z.string(),
    /** @deprecated Use targetStartDateType instead */
    shouldUseExactTargetStartDate: z.boolean().optional(),
    targetStartDateType: TargetStartDateTypeSchema.optional(),
    exactTargetStartDate: z.string(),
    relativeTargetStartDate: z.string(),
    autoStartEnabled: z.boolean(),
    skip: SkipSchema.optional(),
    description: z.string().optional(),
  }),
})

export type CreateAssessmentBlockConfig = z.infer<typeof CreateAssessmentBlockConfigSchema>

// Price Benchmark
export const PriceBenchmarkBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    assignees: z.array(AssigneeEntitySchema),
    targetTimeValue: z.number().nullable(),
    additionalContext: z.string().optional(),
    description: z.string().optional(),
    skip: SkipSchema.optional(),
  }),
})

export type PriceBenchmarkBlockConfig = z.infer<typeof PriceBenchmarkBlockConfigSchema>

export const InherentRiskAssessmentBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: ApprovalBlockConfigPayloadSchema,
})

export type InherentRiskAssessmentBlockConfig = z.infer<typeof InherentRiskAssessmentBlockConfigSchema>

export const SupplierOnlineResourcesBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    description: z.string().optional(),
    assignees: z.array(AssigneeEntitySchema),
    targetTimeValue: z.number().nullable(),
  }),
})

export type SupplierOnlineResourcesBlockConfig = z.infer<typeof SupplierOnlineResourcesBlockConfigSchema>

const externalOperationSubTypes = [
  'ALBERTSONS-CONVERT-TO-CSV',
  'IRONCLAD-COMPLETE-WORKFLOW',
  'IRONCLAD-INITIATE-WORKFLOW',
  'IRONCLAD-WAIT-FOR-COMPLETION',
  'IRONCLAD-UPDATE-WORKFLOW-APPROVAL',
  'IRONCLAD-GET-DOCUMENT',
  'JIRA-CREATE-TICKET',
  'JIRA-SERVICE-MANAGEMENT-CREATE-TICKET',
  'CREDITSAFE-GENERATE_REPORT',
  'DOCUSIGN-DOCUMENT-UPLOAD',
  'DOCUSIGN-CLM-UPLOAD-DOCUMENT',
  'DOCUSIGN-ESIGN-CREATE-ENVELOPE',
  'PROCESS-UNITY-IMPORT-EXPORT',
  'TPRM-CONTINUOUS-MONITORING-POINT-IN-TIME-CHECK',
  'PANDADOC-CREATE-AGREEMENT',
  'ADOBE-SIGN-CREATE-AGREEMENT',
  'MESH-CREATE-VIRTUAL-CARD',
  'NOOP-TEST',
  'ONE-TRUST-CREATE-ASSESSMENT',
  'ONE-TRUST-CREATE-VENDOR',
  'ZAPIER-ZAP',
  'ZENDESK-CREATE-TICKET',
  'HTTP-CUSTOM-INTEGRATION',
  'THOMSON-REUTERS-GENERATE-REPORT',
  'SFTP-UPLOAD-FILE',
  'COUPA-CLM-CREATE-CONTRACT',
  'CONTRACTPODAI-CREATE-CONTRACT',
  'TRULIOO-TRIGGER-WORKFLOW',
  'GOOGLE-DRIVE-UPLOAD-FILE',
  'LINKSQUARES-CREATE-AGREEMENT',
  'LINKSQUARES-APPROVE-TASK',
  'LINKSQUARES-PULL-COMPLETED-AGREEMENT',
  'LINKSQUARES-CREATE-AND-PULL-AGREEMENT',
  'CONGA-CONTRACTS-CREATE-CONTRACT',
  'PUNCHOUT-GENERIC-CREATE-ORDER-REQUEST',
  'PUNCHOUT-AMAZON-BUSINESS-CREATE-ORDER-REQUEST',
  'PUNCHOUT-UNITE-CREATE-ORDER-REQUEST',
  'PUNCHOUT-CDW-CREATE-ORDER-REQUEST',
  'TROPIC-CREATE-CONTRACT',
  'TROPIC-PRICE-BENCHMARK',
] as const

export const ExternalOperationSubTypeSchema = z.enum(externalOperationSubTypes)
export type ExternalOperationSubType = z.infer<typeof ExternalOperationSubTypeSchema>

// External Operation
export const ExternalOperationBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    subType: ExternalOperationSubTypeSchema,
    iconSrc: z.string().optional(),
    iconFilestoreId: z.string().optional(),
    customIntegrationId: z.string().optional(),
    isCustomAccountingIntegration: z.boolean().optional(),
    label: z.string(),
    operation: z.string(), // TODO exp-1389 consolidate operation and sybType
    integration: z.enum(globalIntegrations),
    targetTimeValue: z.number().nullable(),
    shouldMapOutputToForm: z.boolean().optional(),
    assignees: z.array(AssigneeEntitySchema),
    taskDistribution: TaskDistributionSchema.optional(),
    // TODO: Change this to just a single form template ID?
    // Should only be 1 task but keeping this in sync with the other blocks
    tasks: z.array(BlockTaskSchema).optional(),
    // A `form-field` entry's `value` is a question name, but `source` and `value` are independent
    // fields of one object rather than branches of a union, so no annotation can name the question
    // without also naming every static and request-context value.
    inputMapping: externalOperationInputMappingSchema.optional(),
    // Same shape problem on `formData`: a `file` entry's `value` is a question name, a `text`
    // entry's is a literal.
    httpMapping: externalOperationHttpMappingSchema.optional(),
    sftpMapping: externalOperationSftpMappingSchema.optional(),
    // Keyed by question name or question id depending on the `keyBy` its caller passes, which the
    // config does not record: `fillInExternalOperationTask` resolves with 'name', `fillInFormFromOutputs`
    // with 'id'.
    outputMapping: externalOperationOutputMappingSchema.optional(),
    // May contain string. Keyed by whatever the integration's operation declares, so nothing here
    // names a row of ours.
    operationConfig: z.record(z.string(), z.array(z.string())).optional(),
    input: z
      .object({
        variables: z.array(VariableSchema),
        required: z.array(z.string()),
        allowAdditionalVariables: z.boolean(),
        customVariables: z.array(VariableSchema).optional(),
      })
      .optional(),
    output: z.object({ variables: z.array(VariableSchema) }).optional(),
    primaryOutputConfig: primaryOutputConfigSchema.optional(),
    description: z.string().optional(),
    polling: PollingConfigurationSchema.optional(),
    skip: SkipSchema.optional(),
    // A failed execution still completes the step (PASS, error recorded as a task error) instead of parking it in ERROR
    // Note: this flag will be ignored if there are *any* output variables for the operation.
    continueOnFailure: z.boolean().optional(),
    hideFromTimeline: z.boolean().optional(),
  }),
})

export type ExternalOperationBlockConfig = z.infer<typeof ExternalOperationBlockConfigSchema>

// External Procedure
export const ExternalProcedureBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    description: z.string().optional(),
    procedureTemplateId: ref('procedureTemplate'),
    customIntegrationId: z.string(),
    integration: z.enum(globalIntegrations),
    isCustomAccountingIntegration: z.boolean().optional(),
    iconFilestoreId: z.string().optional(),
    targetTimeValue: z.number().nullable(),
    assignees: z.array(AssigneeEntitySchema),
    taskDistribution: TaskDistributionSchema.optional(),
    // Only ever holds the one task the block creates; `tasks[0].formTemplateId` locates the form that
    // form-field input mappings read from, matching EXTERNAL_OPERATION.
    tasks: z.array(BlockTaskSchema).optional(),
    inputMapping: externalOperationInputMappingSchema.optional(),
    // Derived from the procedure template's inputSchema/outputSchema by the block panel. `variableId` on
    // each Variable is the schema property name, which is what the run input and output are keyed by.
    input: z
      .object({
        variables: z.array(VariableSchema),
        required: z.array(z.string()),
      })
      .optional(),
    output: z.object({ variables: z.array(VariableSchema) }).optional(),
    shouldMapOutputToForm: z.boolean().optional(),
    // Keyed by form question id, not name — `fillInFormFromOutputs` resolves with `keyBy: 'id'`.
    outputMapping: outputMappingSchema.optional(),
    skip: SkipSchema.optional(),
  }),
})

export type ExternalProcedureBlockConfig = z.infer<typeof ExternalProcedureBlockConfigSchema>

export const CreateAgreementBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    assignees: z.array(AssigneeEntitySchema),
    taskDistribution: TaskDistributionSchema.optional(),
    tasks: z.array(BlockTaskSchema),
    targetTimeValue: z.number().nullable(),
    notificationMessage: NotificationMessageSchema,
    shouldCompleteAutomatically: z.boolean().optional(),
    skip: SkipSchema.optional(),
    description: z.string().optional(),
    // Absent = copy. Override which tag sources feed the prefilled `tags` question.
    copyRequestTags: z.boolean().optional(),
    copyAutoGeneratedTags: z.boolean().optional(),
    copyLinkedAgreementTags: z.boolean().optional(),
  }),
})

export type CreateAgreementBlockConfig = z.infer<typeof CreateAgreementBlockConfigSchema>

export const CreatePOBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    assignees: z.array(AssigneeEntitySchema),
    taskDistribution: TaskDistributionSchema.optional(),
    tasks: z.array(BlockTaskSchema),
    targetTimeValue: z.number().nullable(),
    notificationMessage: NotificationMessageSchema,
    skip: SkipSchema.optional(),
    description: z.string().optional(),
    forceManualCompletion: z.boolean().optional(),
    escalateToManager: z.boolean().optional(),
    syncWithAccountingIntegration: z.boolean().optional(),
  }),
})

export type CreatePOBlockConfig = z.infer<typeof CreatePOBlockConfigSchema>

export const CancelAgreementBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    assignees: z.array(AssigneeEntitySchema),
    taskDistribution: TaskDistributionSchema.optional(),
    tasks: z.array(BlockTaskSchema),
    targetTimeValue: z.number().nullable(),
    notificationMessage: NotificationMessageSchema,
    shouldCompleteAutomatically: z.boolean().optional(),
    skip: SkipSchema.optional(),
    description: z.string().optional(),
  }),
})

export type CancelAgreementBlockConfig = z.infer<typeof CancelAgreementBlockConfigSchema>

export const CancelAssessmentsBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    description: z.string().optional(),
    skip: SkipSchema.optional(),
    shouldCompleteAutomatically: z.boolean(),
    assignees: z.array(AssigneeEntitySchema),
    taskDistribution: TaskDistributionSchema.optional(),
    tasks: z.array(FillInFormTaskSchema),
    targetTimeValue: z.number().nullable(),
    notificationMessage: NotificationMessageSchema,
  }),
})

export type CancelAssessmentsBlockConfig = z.infer<typeof CancelAssessmentsBlockConfigSchema>

export const RenewalWorkflowSchema = z.object({
  id: ref('workflow'),
  title: z.string(),
})

export type RenewalWorkflow = z.infer<typeof RenewalWorkflowSchema>

// `id` keys the request's form responses at renewal-creation time. It holds an intake question name
// for an assignee variable, but also the built-in `requestor`, which is not a question — so declaring
// it as one would invent a reference to a row that does not exist.
const RenewalInitialWorkflowAssigneeSchema = z.object({
  type: z.literal(AssigneeTypeEnum.VARIABLE),
  id: z.string(),
  value: z.string(),
})

export const RenewalWorkflowConfigSchema = z.object({
  renewalAutoStartWorkflow: z.boolean(),
  renewalWorkflow: RenewalWorkflowSchema,
  renewalAssessmentNotice: z.number(),
  renewalInitialWorkflowAssignee: RenewalInitialWorkflowAssigneeSchema,
})

export type RenewalWorkflowConfig = z.infer<typeof RenewalWorkflowConfigSchema>

export const CreateRenewalBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    targetTimeValue: z.number().nullable(),
    assignees: z.array(AssigneeEntitySchema),
    taskDistribution: TaskDistributionSchema.optional(),
    notificationMessage: NotificationMessageSchema,
    shouldCompleteAutomatically: z.boolean(),
    tasks: z.array(FillInFormTaskSchema),
    workflowConfig: RenewalWorkflowConfigSchema,
    skip: SkipSchema.optional(),
    description: z.string().optional(),
    escalateToManager: z.boolean().optional(),
  }),
})

export type CreateRenewalBlockConfig = z.infer<typeof CreateRenewalBlockConfigSchema>

// Notification
export const NotificationBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    assignees: z.array(AssigneeEntitySchema),
    taskDistribution: TaskDistributionSchema.optional(),
    label: z.string(),
    notificationMessage: NotificationMessageSchema,
    description: z.string().optional(),
    // Older notification blocks don't store the key; readers treat absent as no external recipients.
    externalRecipientEmails: z.array(z.string()).optional(),
    skip: SkipSchema.optional(),
  }),
})

export type NotificationBlockConfig = z.infer<typeof NotificationBlockConfigSchema>

// Notify Slack
export const NotifySlackBlockConfigSchema = z.object({
  ...baseBlockConfig,
  payload: z.object({
    label: z.string(),
    description: z.string().optional(),
    channelId: z.string(),
    channelName: z.string(),
    notificationMessage: NotificationMessageSchema,
    // Absent means comment sync is on: blocks configured before the toggle existed have no value
    // here, and configs are never backfilled.
    disableCommentSync: z.boolean().optional(),
    skip: SkipSchema.optional(),
  }),
})

export type NotifySlackBlockConfig = z.infer<typeof NotifySlackBlockConfigSchema>

/**
 * The schema for each block type's config, for a reader holding a stored node that knows its
 * `blockType` only at runtime — the value-level counterpart of `BlockConfigByType`.
 *
 * Exhaustive by construction: a new `BlockTypeEnum` member does not compile here until it has a
 * schema.
 */
export const blockConfigSchemaByType = {
  [BlockTypeEnum.APPROVAL]: ApprovalBlockConfigSchema,
  [BlockTypeEnum.AUCTION]: AuctionBlockConfigSchema,
  [BlockTypeEnum.BUDGET_APPROVAL]: BudgetApprovalBlockConfigSchema,
  [BlockTypeEnum.CANCEL_AGREEMENT]: CancelAgreementBlockConfigSchema,
  [BlockTypeEnum.CANCEL_ASSESSMENTS]: CancelAssessmentsBlockConfigSchema,
  [BlockTypeEnum.CHILD_REQUEST_START]: LabelOnlyBlockConfigSchema,
  [BlockTypeEnum.COMMIT_SUPPLIER_DATA]: CommitSupplierDataBlockConfigSchema,
  [BlockTypeEnum.CREATE_AGREEMENT]: CreateAgreementBlockConfigSchema,
  [BlockTypeEnum.CREATE_ASSESSMENT]: CreateAssessmentBlockConfigSchema,
  [BlockTypeEnum.CREATE_PO]: CreatePOBlockConfigSchema,
  [BlockTypeEnum.CREATE_RENEWAL]: CreateRenewalBlockConfigSchema,
  [BlockTypeEnum.DATA_TRANSFORMATION]: DataTransformationBlockConfigSchema,
  [BlockTypeEnum.ENGAGEMENT]: EngagementBlockConfigSchema,
  [BlockTypeEnum.EXTERNAL_OPERATION]: ExternalOperationBlockConfigSchema,
  [BlockTypeEnum.EXTERNAL_PROCEDURE]: ExternalProcedureBlockConfigSchema,
  [BlockTypeEnum.HTTP]: HttpBlockConfigSchema,
  [BlockTypeEnum.INHERENT_RISK_ASSESSMENT]: InherentRiskAssessmentBlockConfigSchema,
  [BlockTypeEnum.INTAKE]: IntakeBlockConfigSchema,
  [BlockTypeEnum.MILESTONE]: LabelOnlyBlockConfigSchema,
  [BlockTypeEnum.NOTIFICATION]: NotificationBlockConfigSchema,
  [BlockTypeEnum.NOTIFY_SLACK]: NotifySlackBlockConfigSchema,
  [BlockTypeEnum.PRICE_BENCHMARK]: PriceBenchmarkBlockConfigSchema,
  [BlockTypeEnum.RENEWAL_ASSESSMENT]: RenewalAssessmentBlockConfigSchema,
  [BlockTypeEnum.RFX]: RfxBlockConfigSchema,
  [BlockTypeEnum.SUB_WORKFLOW_START]: LabelOnlyBlockConfigSchema,
  [BlockTypeEnum.SUPPLIER_ONBOARDING]: SupplierOnboardingBlockConfigSchema,
  [BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES]: SupplierOnlineResourcesBlockConfigSchema,
  [BlockTypeEnum.WAITING]: WaitingBlockConfigSchema,
} satisfies { [K in BlockTypeEnum]: z.ZodType<BlockConfigByType[K]> }

export type DefaultConfig = {
  [BlockTypeEnum.APPROVAL]: {
    type: BlockTypeEnum.APPROVAL
    config: ApprovalBlockConfig
  }
  [BlockTypeEnum.SUPPLIER_ONBOARDING]: {
    type: BlockTypeEnum.SUPPLIER_ONBOARDING
    config: SupplierOnboardingBlockConfig
  }
  [BlockTypeEnum.MILESTONE]: {
    type: BlockTypeEnum.MILESTONE
    config: {
      payload: {
        label: string
      }
    }
  }
  [BlockTypeEnum.CHILD_REQUEST_START]: {
    type: BlockTypeEnum.CHILD_REQUEST_START
    config: LabelOnlyBlockConfig
  }
  [BlockTypeEnum.SUB_WORKFLOW_START]: {
    type: BlockTypeEnum.SUB_WORKFLOW_START
    config: LabelOnlyBlockConfig
  }
  [BlockTypeEnum.NOTIFICATION]: {
    type: BlockTypeEnum.NOTIFICATION
    config: NotificationBlockConfig
  }
  [BlockTypeEnum.ENGAGEMENT]: {
    type: BlockTypeEnum.ENGAGEMENT
    config: EngagementBlockConfig
  }
  [BlockTypeEnum.BUDGET_APPROVAL]: {
    type: BlockTypeEnum.BUDGET_APPROVAL
    config: BudgetApprovalBlockConfig
  }
  [BlockTypeEnum.HTTP]: {
    type: BlockTypeEnum.HTTP
    config: HttpBlockConfig
  }
  [BlockTypeEnum.CREATE_RENEWAL]: {
    type: BlockTypeEnum.CREATE_RENEWAL
    config: CreateRenewalBlockConfig
  }
  [BlockTypeEnum.COMMIT_SUPPLIER_DATA]: {
    type: BlockTypeEnum.COMMIT_SUPPLIER_DATA
    config: CommitSupplierDataBlockConfig
  }
  [BlockTypeEnum.CREATE_AGREEMENT]: {
    type: BlockTypeEnum.CREATE_AGREEMENT
    config: CreateAgreementBlockConfig
  }
  [BlockTypeEnum.CANCEL_AGREEMENT]: {
    type: BlockTypeEnum.CANCEL_AGREEMENT
    config: CancelAgreementBlockConfig
  }
  [BlockTypeEnum.CANCEL_ASSESSMENTS]: {
    type: BlockTypeEnum.CANCEL_ASSESSMENTS
    config: CancelAssessmentsBlockConfig
  }
  [BlockTypeEnum.CREATE_PO]: {
    type: BlockTypeEnum.CREATE_PO
    config: CreatePOBlockConfig
  }
  [BlockTypeEnum.RFX]: {
    type: BlockTypeEnum.RFX
    config: RfxBlockConfig
  }
  [BlockTypeEnum.AUCTION]: {
    type: BlockTypeEnum.AUCTION
    config: AuctionBlockConfig
  }
  [BlockTypeEnum.CREATE_ASSESSMENT]: {
    type: BlockTypeEnum.CREATE_ASSESSMENT
    config: CreateAssessmentBlockConfig
  }
  [BlockTypeEnum.PRICE_BENCHMARK]: {
    type: BlockTypeEnum.PRICE_BENCHMARK
    config: PriceBenchmarkBlockConfig
  }
  [BlockTypeEnum.DATA_TRANSFORMATION]: {
    type: BlockTypeEnum.DATA_TRANSFORMATION
    config: DataTransformationBlockConfig
  }
  [BlockTypeEnum.WAITING]: {
    type: BlockTypeEnum.WAITING
    config: WaitingBlockConfig
  }
  [BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES]: {
    type: BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES
    config: SupplierOnlineResourcesBlockConfig
  }
  [BlockTypeEnum.INHERENT_RISK_ASSESSMENT]: {
    type: BlockTypeEnum.INHERENT_RISK_ASSESSMENT
    config: InherentRiskAssessmentBlockConfig
  }
  [BlockTypeEnum.NOTIFY_SLACK]: {
    type: BlockTypeEnum.NOTIFY_SLACK
    config: NotifySlackBlockConfig
  }
}
